import { supabase } from '../../lib/supabase';
import type { ImpersonationTarget } from '../../utils/userImpersonationOverride';
import { profileRowToImpersonationTarget } from '../../utils/userImpersonationOverride';
import {
  setRoleViewOverride,
  clearRoleViewOverride,
  previewOriginalRole,
} from '../../utils/roleViewOverride';
import { setWorkspaceOrg } from './saasOrg';
import { renamedRoleTitle } from './workspaceLabels';
import { pinImpersonationWorkspace } from './impersonationWorkspace';

export type PlatformImpersonationTargetRow = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
  created_at: string | null;
  is_active: boolean | null;
  member_role: string | null;
};

export async function fetchPlatformImpersonationTargets(
  orgId: string,
  role?: string | null,
): Promise<{ rows: PlatformImpersonationTargetRow[]; error: string | null }> {
  const { data, error } = await supabase.rpc('saas_platform_admin_list_impersonation_targets', {
    p_org_id: orgId,
    p_role: role ?? null,
  });
  if (error) {
    return { rows: [], error: error.message };
  }
  return { rows: (data ?? []) as PlatformImpersonationTargetRow[], error: null };
}

export function impersonationRowToTarget(row: PlatformImpersonationTargetRow): ImpersonationTarget {
  return profileRowToImpersonationTarget({
    id: row.id,
    email: row.email,
    first_name: row.first_name,
    last_name: row.last_name,
    phone: row.phone,
    role: row.role,
    created_at: row.created_at,
    is_active: row.is_active,
  });
}

export function rowsForRole(
  rows: PlatformImpersonationTargetRow[],
  roleId: string,
): PlatformImpersonationTargetRow[] {
  return rows.filter((row) => row.member_role === roleId || (!row.member_role && row.role === roleId));
}

export async function fetchPlatformOrgOwnerTarget(
  orgId: string,
): Promise<{ target: ImpersonationTarget | null; error: string | null }> {
  const { data, error } = await supabase.rpc('saas_platform_admin_org_owner_profile', {
    p_org_id: orgId,
  });
  if (error) {
    return { target: null, error: error.message };
  }
  const profile = (Array.isArray(data) ? data[0] : data) as PlatformImpersonationTargetRow | null;
  if (!profile?.id) {
    return { target: null, error: 'No active org owner found for this workspace.' };
  }
  return {
    target: impersonationRowToTarget({ ...profile, member_role: 'super_admin' }),
    error: null,
  };
}

/** One-click view as a dashboard role — picks a user when available, else previews UI via org owner. */
export async function platformViewAsOrgRole(
  orgId: string,
  roleId: string,
  rows: PlatformImpersonationTargetRow[],
  applyImpersonation: (
    target: ImpersonationTarget,
    options?: {
      workspaceOrgId?: string;
      actorIsPlatformAdmin?: boolean;
      viewAsRole?: string;
      rolePreview?: boolean;
    },
  ) => Promise<void>,
): Promise<void> {
  const matches = rowsForRole(rows, roleId);
  let target: ImpersonationTarget;
  let viewAsRole: string | undefined;
  let rolePreview = false;

  if (matches.length > 0) {
    target = impersonationRowToTarget(matches[0]);
    clearRoleViewOverride(target.id);
    viewAsRole = roleId;
  } else if (roleId === 'super_admin') {
    const owner = await fetchPlatformOrgOwnerTarget(orgId);
    if (owner.error || !owner.target) {
      throw new Error(owner.error ?? 'No org owner found');
    }
    target = owner.target;
    viewAsRole = 'super_admin';
  } else {
    const owner = await fetchPlatformOrgOwnerTarget(orgId);
    if (owner.error || !owner.target) {
      throw new Error(
        `No ${formatImpersonationRoleLabel(roleId)} user in this workspace. Add staff or a customer first.`,
      );
    }
    target = owner.target;
    viewAsRole = roleId;
    rolePreview = true;
    clearRoleViewOverride(target.id);
    setRoleViewOverride(target.id, previewOriginalRole(target.role, 'super_admin'), roleId);
  }

  await setWorkspaceOrg(orgId);
  await applyImpersonation(target, {
    workspaceOrgId: orgId,
    actorIsPlatformAdmin: true,
    viewAsRole,
    rolePreview,
  });
}

export async function platformImpersonateOrgUser(
  orgId: string,
  target: ImpersonationTarget,
  applyImpersonation: (
    target: ImpersonationTarget,
    options?: { workspaceOrgId?: string; actorIsPlatformAdmin?: boolean },
  ) => Promise<void>,
  options?: { actorIsPlatformAdmin?: boolean },
): Promise<void> {
  await setWorkspaceOrg(orgId);
  await applyImpersonation(target, {
    workspaceOrgId: orgId,
    actorIsPlatformAdmin: options?.actorIsPlatformAdmin ?? true,
  });
}

/**
 * Re-apply the org pin for platform "view as" / impersonation.
 * Teams RLS keys off request_org_id() with no platform-admin bypass — without this,
 * supervisedTeams stays empty and SupervisorDashboard shows all zeros.
 */
export async function ensureImpersonationWorkspacePin(actorUserId?: string | null): Promise<string | null> {
  return pinImpersonationWorkspace(actorUserId);
}

export const PLATFORM_VIEW_AS_ROLES = [
  { id: 'super_admin', label: 'Super admin (owner)' },
  { id: 'admin', label: 'Admin' },
  { id: 'manager', label: 'Manager' },
  { id: 'supervisor', label: 'Supervisor' },
  { id: 'employee', label: 'Employee' },
  { id: 'customer', label: 'Customer' },
] as const;

/** Menu label for a dashboard role, using the organisation's own office titles when set. */
export function viewAsRoleLabel(roleId: string, titles?: Partial<Record<string, string>> | null): string {
  const custom = titles?.[roleId]?.trim();
  if (custom) return custom;
  const match = PLATFORM_VIEW_AS_ROLES.find((item) => item.id === roleId);
  return match?.label ?? roleId.replace(/_/g, ' ');
}

export function formatImpersonationRoleLabel(
  role: string,
  memberRole?: string | null,
  titles?: Partial<Record<string, string>> | null,
): string {
  const effective = memberRole || role;
  return renamedRoleTitle(effective) ?? viewAsRoleLabel(effective, titles);
}
